import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useCallback, useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useCreateMutation } from "../../../api/genericHooks";
import { BookContext } from "../../../contexts/BookContext";
import { Step } from "../PrintModal";
import { PrintModalStepper } from "./PrintModalStepper";

type Props = {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  steps: Step[];
  nextStep: () => void;
};

export type PrintOrderParams = {
  first_name: string;
  last_name: string;
  address: string;
  address_line_2?: string;
  city: string;
  zip_code: string;
  country: string;
  phone: string;
};

export const PrintModalStep1: FC<Props> = ({
  open,
  setOpen,
  steps,
  nextStep,
}) => {
  const { t } = useTranslation();
  const { book, print_orders, refresh } = useContext(BookContext);
  const print_order = print_orders?.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0];

  // Form Methods =============================================================
  const GeneratePDF1FormSchema = Yup.object().shape({
    first_name: Yup.string().required(t("errors.required")),
    last_name: Yup.string().required(t("errors.required")),
    address: Yup.string().required(t("errors.required")),
    address_line_2: Yup.string().optional(),
    city: Yup.string().required(t("errors.required")),
    zip_code: Yup.string().required(t("errors.required")),
    country: Yup.string().required(t("errors.required")),
    phone: Yup.string().required(t("errors.required")),
  });

  const defaultValues = useMemo<PrintOrderParams>(() => {
    return {
      first_name: print_order?.first_name || "",
      last_name: print_order?.last_name || "",
      address: print_order?.address || "",
      address_line_2: print_order?.address_line_2 || "",
      city: print_order?.city || "",
      zip_code: print_order?.zip_code || "",
      country: print_order?.country || "",
      phone: print_order?.phone || "",
    };
  }, [book]);

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<PrintOrderParams>({
    defaultValues: defaultValues,
    resolver: yupResolver(GeneratePDF1FormSchema),
  });

  // Mutations ===============================================================
  const updatePrintOrderMutation = useCreateMutation(
    "/update_delivery_address"
  );

  // Form Submit =============================================================
  const onSubmit = useCallback(
    (values: PrintOrderParams) => {
      updatePrintOrderMutation.mutateAsync({ print_order: values }).then(() => {
        toast.success(t("printModal.PrintOrderUpdated"));
        refresh();
        nextStep();
      });
    },
    [refresh, t]
  );

  // After every hook (rules of hooks)
  if (!book) {
    return null;
  }

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-slate-800 px-3 sm:px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 w-full mx-2 sm:mx-0"
          >
            <form
              className="bg-white flex flex-1 flex-col justify-between"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="absolute right-0 top-0 pr-3 pt-3 sm:pr-4 sm:pt-4 block">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded bg-white text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-delicate-500 focus:ring-offset-2"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className="w-5 h-5 sm:w-6 sm:h-6"
                  />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-left sm:ml-4 sm:mt-0 w-full">
                  <DialogTitle
                    as="h3"
                    className="text-xl sm:text-2xl font-semibold text-slate-800 pr-8 sm:pr-0"
                  >
                    {t("printModal.title")}
                  </DialogTitle>
                  <hr className="mt-2 w-full border-gray-200" />

                  <PrintModalStepper steps={steps} />

                  <p className="mt-4 text-sm sm:text-base text-slate-700 font-semibold">
                    {t("printModal.description")}
                  </p>
                  <div className="flex flex-col space-y-4 mt-4">
                    <div className="flex flex-col sm:flex-row w-full space-y-4 sm:space-y-0 sm:space-x-6">
                      <div className="w-full">
                        <label
                          htmlFor="first_name"
                          className="block font-nunito text-sm font-medium text-slate-900"
                        >
                          {t("printModal.fields.first_name")}
                        </label>
                        <div className="mt-2">
                          <input
                            id="first_name"
                            type="text"
                            autoComplete="first_name"
                            className="block w-full font-nunito rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400"
                            {...register("first_name", { required: true })}
                          />
                          {errors.first_name && (
                            <span className="text-rust-400 font-nunito text-sm">
                              {errors.first_name.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-full">
                        <label
                          htmlFor="last_name"
                          className="block text-sm font-nunito font-medium text-slate-900"
                        >
                          {t("printModal.fields.last_name")}
                        </label>
                        <div className="mt-2">
                          <input
                            id="last_name"
                            type="text"
                            autoComplete="last_name"
                            className="block w-full font-nunito rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400"
                            {...register("last_name", { required: true })}
                          />
                          {errors.last_name && (
                            <span className="text-rust-400 font-nunito text-sm">
                              {errors.last_name.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col w-full">
                      <div className="w-full">
                        <label
                          htmlFor="address"
                          className="block font-nunito text-sm font-medium text-slate-900"
                        >
                          {t("printModal.fields.address")}
                        </label>
                        <div className="mt-2">
                          <input
                            id="address"
                            type="text"
                            autoComplete="address"
                            className="block w-full font-nunito rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400"
                            {...register("address", { required: true })}
                          />
                          {errors.address && (
                            <span className="text-rust-400 font-nunito text-sm">
                              {errors.address.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col w-full">
                      <div className="w-full">
                        <label
                          htmlFor="address_line_2"
                          className="block font-nunito text-sm font-medium text-slate-900"
                        >
                          {t("printModal.fields.address_line_2")}
                        </label>
                        <div className="mt-2">
                          <input
                            id="address_line_2"
                            type="text"
                            autoComplete="address_line_2"
                            className="block w-full font-nunito rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400"
                            {...register("address_line_2")}
                          />
                          {errors.address_line_2 && (
                            <span className="text-rust-400 font-nunito text-sm">
                              {errors.address_line_2.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row w-full space-y-4 sm:space-y-0 sm:space-x-6">
                      <div className="w-full">
                        <label
                          htmlFor="zip_code"
                          className="block font-nunito text-sm font-medium text-slate-900"
                        >
                          {t("printModal.fields.zip_code")}
                        </label>
                        <div className="mt-2">
                          <input
                            id="zip_code"
                            type="text"
                            autoComplete="zip_code"
                            className="block w-full font-nunito rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400"
                            {...register("zip_code", { required: true })}
                          />
                          {errors.zip_code && (
                            <span className="text-rust-400 font-nunito text-sm">
                              {errors.zip_code.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-full">
                        <label
                          htmlFor="city"
                          className="block text-sm font-nunito font-medium text-slate-900"
                        >
                          {t("printModal.fields.city")}
                        </label>
                        <div className="mt-2">
                          <input
                            id="city"
                            type="text"
                            autoComplete="city"
                            className="block w-full font-nunito rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400"
                            {...register("city", { required: true })}
                          />
                          {errors.city && (
                            <span className="text-rust-400 font-nunito text-sm">
                              {errors.city.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row w-full space-y-4 sm:space-y-0 sm:space-x-6">
                      <div className="w-full">
                        <label
                          htmlFor="country"
                          className="block font-nunito text-sm font-medium text-slate-900"
                        >
                          {t("printModal.fields.country")}
                        </label>
                        <div className="mt-2">
                          <input
                            id="country"
                            type="text"
                            autoComplete="country"
                            className="block w-full font-nunito rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400"
                            {...register("country", { required: true })}
                          />
                          {errors.country && (
                            <span className="text-rust-400 font-nunito text-sm">
                              {errors.country.message}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-full">
                        <label
                          htmlFor="phone"
                          className="block text-sm font-nunito font-medium text-slate-900"
                        >
                          {t("printModal.fields.phone")}
                        </label>
                        <div className="mt-2">
                          <input
                            id="phone"
                            type="text"
                            autoComplete="phone"
                            className="block w-full font-nunito rounded bg-white px-3 py-2 text-base text-slate-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-slate-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-rust-400"
                            {...register("phone", { required: true })}
                          />
                          {errors.phone && (
                            <span className="text-rust-400 font-nunito text-sm">
                              {errors.phone.message}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3 sm:gap-0">
                <button
                  type="submit"
                  className="inline-flex w-full justify-center rounded bg-rust-50 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-rust-300 hover:bg-rust-100 sm:w-auto"
                >
                  {t("printModal.save")}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
